import { fromJS, List, Map, Set } from 'immutable'
import { setIn } from 'utils/immutable'
import { prop } from 'ramda'
import * as userActions from 'actions/user.js'
import strings from 'utils/strings'
import { createReducer } from './utils'

const defaultTableOrder = [
  strings.vehicles,
  strings.reference_parameters,
  strings.support_parameters,
  strings.features,
]

export const initialState = fromJS({
  fetchingUserData: false,
  userData: {
    userName: '',
    appRoles: [],
    settings: Map({
      dashboard_tables_order: defaultTableOrder,
    }),
  },
  userDataError: null,
  favoriteVehicles: [],
  fetchingFavoriteVehicles: false,
  fetchingFavoriteVehiclesError: null,
  processingFavoritedVehicle: false,
  // favoritingCount tracks concurrent in-flight favorite/unfavorite/update
  // operations. The boolean processingFavoritedVehicle is kept for reducer
  // compatibility but App.jsx now uses favoritingCount > 0 for the overlay.
  favoritingCount: 0,
  favoritingVehicleError: null,
  removingVehicleFavoriteError: null,
  showFavoriteVehiclesFlyout: false,
  showFavoriteAddedNotification: false,
  showFavoriteRemovedNotification: false,
  showFavoriteUpdatedNotification: false,
  showFavoriteErrorNotification: false,
  alerts: Map(),
  fetchingAlert: false,
  alertError: null,
})

const handlers = {
  [userActions.REQUESTING_USER_FAVORITE_VEHICLES]:
    requestingUserFavoriteVehicles,
  [userActions.USER_FAVORITE_VEHICLES_RECEIVED]: userFavoriteVehiclesReceived,
  [userActions.USER_FAVORITE_VEHICLES_NOT_RECEIVED]:
    userFavoriteVehiclesNotReceived,
  [userActions.SAVING_ALERT]: requestingAlert,
  [userActions.REQUESTING_ALERT]: requestingAlert,
  [userActions.ALERT_SAVED]: alertSaved,
  [userActions.ALERT_RECEIVED]: alertReceived,
  [userActions.ALERT_NOT_SAVED]: alertNotReceived,
  [userActions.ALERT_NOT_RECEIVED]: alertNotReceived,
  [userActions.SET_ALERT]: setAlert,
  [userActions.REQUESTING_RECENTLY_VIEWED_DATA]: requestingRecentlyViewedData,
  [userActions.ADD_VEHICLE_TO_RECENTLY_VIEWED]: addVehicleToRecentlyViewed,
  [userActions.CLEAR_RECENTLY_VIEWED_SELECTED]: clearRecentlyViewedSelected,
  [userActions.TOGGLE_RECENTLY_VIEWED_SELECTED]: toggleRecentlyViewedSelected,
  [userActions.RECENTLY_VIEWED_DATA_RECEIVED]: recentlyViewedDataReceived,
  [userActions.RECENTLY_VIEWED_DATA_NOT_RECEIVED]:
    recentlyViewedDataNotReceived,
  [userActions.APPLY_RECENTLY_VIEWED_LOCAL_CACHE]: applyLocalVehicleCache,
  [userActions.FAVORITING_USER_VEHICLE]: favoritingUserVehicle,
  [userActions.USER_VEHICLE_FAVORITED]: userVehicleFavorited,
  [userActions.FAVORITE_USER_VEHICLE_FAILED]: favoriteUserVehicleFailed,
  [userActions.REMOVING_FAVORITED_USER_VEHICLE]: removingFavoritedUserVehicle,
  [userActions.USER_VEHICLE_FAVORITE_REMOVED]: userVehicleFavoriteRemoved,
  [userActions.USER_VEHICLE_FAVORITE_REMOVAL_FAILED]:
    userVehicleFavoriteRemovalFailed,
  [userActions.SET_FAVORITE_VEHICLES_FLYOUT_VISIBLE]:
    setFavoriteVehiclesFlyoutVisible,
  [userActions.CLEAR_FAVORITE_ADDED_NOTIFICATION]:
    clearFavoriteAddedNotification,
  [userActions.CLEAR_FAVORITE_REMOVED_NOTIFICATION]:
    clearFavoriteRemovedNotification,
  [userActions.CLEAR_FAVORITE_UPDATED_NOTIFICATION]:
    clearFavoriteUpdatedNotification,
  [userActions.CLEAR_FAVORITE_ERROR_NOTIFICATION]:
    clearFavoriteErrorNotification,
  [userActions.UPDATING_VEHICLE_FAVORITE]: updatingVehicleFavorite,
  [userActions.USER_VEHICLE_FAVORITE_UPDATED]: userVehicleFavoriteUpdated,
  [userActions.USER_VEHICLE_FAVORITE_UPDATE_FAILED]:
    userVehicleFavoriteUpdateFailed,
  [userActions.REORDERING_FAVORITE_VEHICLES]: reorderingFavoriteVehicles,
  [userActions.FAVORITE_VEHICLES_REORDERED]: favoriteVehiclesReordered,
  [userActions.FAVORITE_VEHICLES_REORDER_CONFIRMED]:
    favoriteVehiclesReorderConfirmed,
  [userActions.FAVORITE_VEHICLES_REORDER_FAILED]: favoriteVehiclesReorderFailed,
  [userActions.EULA_DISCLAIMER_ACCEPTED]: eulaDisclaimerAccepted,
  [userActions.GET_USER_INFO]: userDataRecieved,
  [userActions.UPDATE_USER_SETTINGS]: updateUserSettings,
  [userActions.UPDATE_USER_SETTING]: updateUserSettings,
  [userActions.SHOW_RELEASE_NOTES]: showReleaseNotes,
}

export default createReducer(initialState, handlers)

function showReleaseNotes(state, { show }) {
  return setIn(['userData', 'showReleaseNotes'], show, state)
}

function updateUserSettings(state, { settings }) {
  const address = ['userData', 'settings']
  const existing = state.getIn(address, Map())
  return setIn(address, existing.merge(settings), state)
}

function requestingUserFavoriteVehicles(state) {
  return state.merge({
    fetchingFavoriteVehicles: true,
  })
}

function userFavoriteVehiclesReceived(state, action) {
  // Defensive: guard against undefined in case the response shape ever changes
  // and action.data is not populated (e.g. if an endpoint starts returning a
  // `result` field, apiCall.js would spread data flat instead of nesting it).
  const favoriteVehicles = action.data ?? []

  // Sort by favoriteDateAdded descending (newest first)
  const sortedVehicles = [...favoriteVehicles].sort((a, b) => {
    if (!a.favoriteDateAdded && !b.favoriteDateAdded) return 0
    if (!a.favoriteDateAdded) return 1
    if (!b.favoriteDateAdded) return -1
    return new Date(b.favoriteDateAdded) - new Date(a.favoriteDateAdded)
  })

  return state.merge({
    favoriteVehicles: fromJS(sortedVehicles),
    fetchingFavoriteVehicles: false,
  })
}

function userFavoriteVehiclesNotReceived(state, action) {
  const { error } = action
  return state.merge({
    fetchingFavoriteVehicles: false,
    fetchingFavoriteVehiclesError: error,
  })
}

function userDataRecieved(state, { data }) {
  const { appRights, settings, ...rest } = data
  const existingSettings = state.getIn(['userData', 'settings'], Map())
  return state
    .set('userData', fromJS(rest))
    .setIn(['userData', 'appRights'], Set(appRights))
    .setIn(['userData', 'settings'], existingSettings.merge(settings))
}

// Share request state.  Not really even used.
// XXX Then why are we maintaining request status all over the place?
// Need generic handling of request errors.
function requestingAlert(state, action) {
  return state.set('fetchingAlert', true)
}

function alertSaved(state) {
  const ret = state.merge({
    fetchingAlert: false,
  })
  return ret
}

function alertReceived(state, { data }) {
  const alertDictionary = data.reduce(
    (acc, curr) => ({
      ...acc,
      [prop('zone', curr)]: {
        ...curr,
      },
    }),
    {}
  )
  return state.merge({
    fetchingAlert: false,
    alerts: fromJS(alertDictionary),
  })
}

function alertNotReceived(state, action) {
  return state.merge({
    fetchingAlert: false,
    alertError: action.error,
  })
}

function setAlert(state, { data }) {
  const { zone } = data
  return setIn(['alerts', zone], data, state)
}

function requestingRecentlyViewedData(state) {
  return state.set('fetchingRecentlyViewedData', true)
}

function clearRecentlyViewedSelected(state, action) {
  const subResourceState = state.get(action.subResource)

  const newSubResourceState = subResourceState.map((item) =>
    item.set('selected', false)
  )

  return state.set(action.subResource, newSubResourceState)
}

function toggleRecentlyViewedSelected(state, action) {
  return state.setIn(
    [action.subResource, action.index, 'selected'],
    !state.getIn([action.subResource, action.index, 'selected'])
  )
}

function recentlyViewedDataReceived(state, action) {
  const formatRecentlyViewedData = (data) => {
    const newData = {}
    Object.keys(data).forEach((key) => {
      const itemArr = data[key].map((item) => {
        const newItem = item
        newItem.selected = false
        return newItem
      })
      newData[key] = itemArr
    })
    return newData
  }

  const serverData = action.data || {
    vehicles: [],
    features: [],
    referenceParameters: [],
    supportParameters: [],
  }

  const newData = formatRecentlyViewedData(serverData)

  // Preserve optimistic entries (vehicles added via addVehicleToRecentlyViewed
  // while user identity was still resolving and persistence was deferred).
  // Without this, the server response silently erases in-flight entries that
  // were never written to localStorage, making them disappear mid-session.
  // For serial collisions the optimistic entry is authoritative for ordering;
  // server-supplied fields are merged in as the base so no server-only data
  // is lost.  The server viewedDate takes precedence (see merge below).
  const existing = state.get('recentlyViewedVehicles')
  if (existing && existing.size > 0) {
    const serverBySerial = {}
    newData.vehicles.forEach((v) => {
      serverBySerial[v.serialNumber] = v
    })
    const existingList = existing.toJS()
    const optimistic = existingList.map((v) => {
      const serverEntry = serverBySerial[v.serialNumber] || {}
      // Server viewedDate is authoritative (backed by DB VIEW_TS).
      // Only fall back to the client-side timestamp when the server
      // has no record yet (vehicle just viewed, save still in flight).
      const serverHasDate =
        serverEntry.viewedDate && !serverEntry.viewedDate.startsWith('0001')
      return {
        ...serverEntry,
        ...v,
        viewedDate: serverHasDate ? serverEntry.viewedDate : v.viewedDate,
        selected: false,
      }
    })
    const optimisticSerials = new Set(existingList.map((v) => v.serialNumber))
    const serverOnly = newData.vehicles.filter(
      (v) => !optimisticSerials.has(v.serialNumber)
    )
    newData.vehicles = [...optimistic, ...serverOnly].slice(0, 10)
  } else {
    newData.vehicles = newData.vehicles.slice(0, 10)
  }

  return state.merge({
    fetchingRecentlyViewedData: false,
    recentlyViewedVehicles: newData.vehicles,
    recentlyViewedSupportParameters: newData.supportParameters,
    recentlyViewedReferenceParameters: newData.referenceParameters,
    recentlyViewedFeatures: newData.features,
  })
}

function recentlyViewedDataNotReceived(state, action) {
  return state.merge({
    fetchingRecentlyViewedData: false,
    recentlyViewedDataError: action.error,
  })
}

function applyLocalVehicleCache(state, action) {
  return state.set('recentlyViewedVehicles', fromJS(action.vehicles))
}

const RECENTLY_VIEWED_MAX = 10

function addVehicleToRecentlyViewed(state, action) {
  const { vehicle } = action
  const entry = fromJS({
    ...vehicle,
    viewedDate:
      vehicle.viewedDate && !vehicle.viewedDate.startsWith('0001')
        ? vehicle.viewedDate
        : new Date().toISOString(),
    selected: false,
  })

  // After RECENTLY_VIEWED_DATA_RECEIVED the list is stored as a plain JS array.
  // Normalise to an Immutable List so .filter/.unshift/.slice behave correctly.
  const raw = state.get('recentlyViewedVehicles')
  const existing = List.isList(raw) ? raw : fromJS(raw || [])

  // Remove existing entry for this vehicle (de-duplicate), prepend, cap at max
  const updated = existing
    .filter((v) => v.get('serialNumber') !== vehicle.serialNumber)
    .unshift(entry)
    .slice(0, RECENTLY_VIEWED_MAX)

  return state.set('recentlyViewedVehicles', updated)
}

function favoritingUserVehicle(state, action) {
  // Clear all notifications when starting a new favorite action
  return state
    .merge({
      processingFavoritedVehicle: true,
      showFavoriteAddedNotification: false,
      showFavoriteRemovedNotification: false,
      showFavoriteUpdatedNotification: false,
      showFavoriteErrorNotification: false,
    })
    .update('favoritingCount', (n) => n + 1)
}

function userVehicleFavorited(state, action) {
  // action.vehicle comes from the args spread by apiCall.js — see favoriteVehicle
  // in actions/user.js where args: { serialNumber, vehicle } is passed. If args
  // is ever refactored and the vehicle key renamed, this spread will silently
  // stop including the vehicle data and the favorites list will be corrupted.
  const vehicleWithDate = {
    ...action.vehicle,
    favoriteDateAdded: new Date().toISOString(),
  }

  return state
    .merge({
      processingFavoritedVehicle: false,
      showFavoriteAddedNotification: true,
      favoriteVehicles: state
        .get('favoriteVehicles')
        .unshift(fromJS(vehicleWithDate)),
    })
    .update('favoritingCount', (n) => Math.max(0, n - 1))
}

function favoriteUserVehicleFailed(state, action) {
  return state
    .merge({
      processingFavoritedVehicle: false,
      favoritingVehicleError: action.error,
      showFavoriteErrorNotification: true,
    })
    .update('favoritingCount', (n) => Math.max(0, n - 1))
}

function removingFavoritedUserVehicle(state, action) {
  // Clear all notifications when starting a new remove action
  return state
    .merge({
      processingFavoritedVehicle: true,
      showFavoriteAddedNotification: false,
      showFavoriteRemovedNotification: false,
      showFavoriteUpdatedNotification: false,
      showFavoriteErrorNotification: false,
    })
    .update('favoritingCount', (n) => n + 1)
}

function userVehicleFavoriteRemoved(state, action) {
  return state
    .merge({
      processingFavoritedVehicle: false,
      showFavoriteRemovedNotification: true,
      favoriteVehicles: state
        .get('favoriteVehicles')
        .filter((v) => v.get('serialNumber') !== action.serialNumber),
    })
    .update('favoritingCount', (n) => Math.max(0, n - 1))
}

function setFavoriteVehiclesFlyoutVisible(state, action) {
  // Clear the notifications when the panel is opened or closed
  return state.merge({
    showFavoriteVehiclesFlyout: action.visible,
    showFavoriteAddedNotification: false,
    showFavoriteRemovedNotification: false,
    showFavoriteUpdatedNotification: false,
    showFavoriteErrorNotification: false,
  })
}

function clearFavoriteAddedNotification(state) {
  return state.set('showFavoriteAddedNotification', false)
}

function clearFavoriteRemovedNotification(state) {
  return state.set('showFavoriteRemovedNotification', false)
}

function clearFavoriteUpdatedNotification(state) {
  return state.set('showFavoriteUpdatedNotification', false)
}

function clearFavoriteErrorNotification(state) {
  return state.set('showFavoriteErrorNotification', false)
}

function userVehicleFavoriteRemovalFailed(state, action) {
  return state
    .merge({
      processingFavoritedVehicle: false,
      removingVehicleFavoriteError: action.error,
      showFavoriteErrorNotification: true,
    })
    .update('favoritingCount', (n) => Math.max(0, n - 1))
}

function updatingVehicleFavorite(state) {
  // Clear all notifications when starting an update action
  return state
    .merge({
      processingFavoritedVehicle: true,
      showFavoriteAddedNotification: false,
      showFavoriteRemovedNotification: false,
      showFavoriteUpdatedNotification: false,
      showFavoriteErrorNotification: false,
    })
    .update('favoritingCount', (n) => n + 1)
}

function userVehicleFavoriteUpdated(state, action) {
  const { serialNumber, customDescription } = action
  const favoriteVehicles = state.get('favoriteVehicles')
  const vehicleIndex = favoriteVehicles.findIndex(
    (v) => v.get('serialNumber') === serialNumber
  )

  if (vehicleIndex === -1) {
    return state
      .set('processingFavoritedVehicle', false)
      .update('favoritingCount', (n) => Math.max(0, n - 1))
  }

  return state
    .merge({
      processingFavoritedVehicle: false,
      showFavoriteUpdatedNotification: true,
      favoriteVehicles: favoriteVehicles.setIn(
        [vehicleIndex, 'customDescription'],
        customDescription
      ),
    })
    .update('favoritingCount', (n) => Math.max(0, n - 1))
}

function userVehicleFavoriteUpdateFailed(state, action) {
  return state
    .merge({
      processingFavoritedVehicle: false,
      updatingVehicleFavoriteError: action.error,
      showFavoriteErrorNotification: true,
    })
    .update('favoritingCount', (n) => Math.max(0, n - 1))
}

function reorderingFavoriteVehicles(state) {
  // Increment favoritingCount so the App.jsx loading overlay appears during
  // reorder requests — consistent with add/remove/update favorite behavior.
  return state
    .set('reorderingFavoriteVehicles', true)
    .update('favoritingCount', (n) => n + 1)
}

function favoriteVehiclesReordered(state, action) {
  return state.merge({
    reorderingFavoriteVehicles: false,
    favoriteVehicles: fromJS(action.vehicles),
  })
}

function favoriteVehiclesReorderFailed(state, action) {
  // action.previousVehicles comes from args spread in apiCall — it was captured
  // by reorderFavoriteVehicles before the optimistic dispatch so we can undo
  // the UI reorder when the server rejects the change.
  //
  // We deliberately do NOT blindly restore the snapshot, because a concurrent
  // favoriteVehicle (add) or removeVehicleFavorite success may have arrived
  // while the reorder was in-flight — blindly restoring the snapshot would
  // silently drop those concurrent changes.
  //
  // Instead we re-apply the original ordering to the CURRENT list:
  //   1. Build a position map from previousVehicles (serialNumber → index).
  //   2. Sort the current list by that map, placing items not in the snapshot
  //      (concurrently added) at the end, preserving their relative order.
  //   3. Items removed from favorites since the snapshot are naturally absent
  //      from the current list and don't need special handling.
  //
  // IMPORTANT: this restore logic assumes at most one reorder is in-flight at
  // a time. That guarantee is provided by the static id: REORDERING_FAVORITE_VEHICLES
  // in reorderFavoriteVehicles (actions/user.js) — the de-dup mechanism prevents
  // a second concurrent reorder from being dispatched while the first is pending.
  // If that de-dup guarantee is ever removed, this restore could partially overwrite
  // a second reorder's optimistic update, leaving the list in an inconsistent state.
  const currentVehicles = state.get('favoriteVehicles')

  const restored = action.previousVehicles
    ? (() => {
        const previousOrder = new Map(
          action.previousVehicles.map((v, i) => [v.serialNumber, i])
        )
        const sorted = currentVehicles.toJS().sort((a, b) => {
          const posA = previousOrder.has(a.serialNumber)
            ? previousOrder.get(a.serialNumber)
            : Infinity
          const posB = previousOrder.has(b.serialNumber)
            ? previousOrder.get(b.serialNumber)
            : Infinity
          return posA - posB
        })
        return fromJS(sorted)
      })()
    : currentVehicles

  return state
    .merge({
      reorderingFavoriteVehicles: false,
      reorderingFavoriteVehiclesError: action.error,
      // Revert the optimistic UI reorder while preserving any concurrent
      // add/remove operations that arrived during the in-flight request.
      favoriteVehicles: restored,
    })
    .update('favoritingCount', (n) => Math.max(0, n - 1))
}

/**
 * API confirmation that the reorder was persisted on the server.
 * The list is already correct from the optimistic dispatch — no state change
 * to favoriteVehicles is needed here. We only clear the in-flight flag to
 * ensure consistent loading state if the confirmation races the optimistic update.
 */
function favoriteVehiclesReorderConfirmed(state) {
  return state
    .set('reorderingFavoriteVehicles', false)
    .update('favoritingCount', (n) => Math.max(0, n - 1))
}

function eulaDisclaimerAccepted(state, action) {
  return state.setIn(['userData', 'eulaAccepted'], action.eulaAccepted)
}
